import {createReducer} from '@reduxjs/toolkit';
import {unionWith, isEqual} from 'lodash';
import {
    getProjects,
    getProfiles,
    getFeedbacks,
    getLastView,
    getLastViewFeedbacks,
    getLastViewProfiles,
    deleteFeedback,
    updateFeedbacks,
    updateLastView,
    getTemplates,
    searchProfilesWithProjects,
    createOptionalFeedback,
    deleteOptionalFeedback,
    clearProfilesWithProjects
} from './actions';
import {ProjectInfoDto} from '../../../dto/ProjectInfoDto';
import {SearchProfileDto} from '../../../dto/SearchProfileDto';
import {MarkTemplateDto} from '../../../dto/MarkTemplateDto';
import {ViewDto} from '../../../dto/ViewDto';
import {FeedbackDto} from '../../../dto/FeedbackDto';
import {ProfileDto} from '../../../dto/ProfileDto';
import {ProjectTeamDto, ProjectFeedbacksDto} from '../typedef';

type InitialState = {
    isFetching: boolean;
    projects: ProjectInfoDto[];
    profiles: ProjectTeamDto[];
    feedbacks: ProjectFeedbacksDto[];
    lastView: ViewDto[];
    lastViewFeedbacks: FeedbackDto[];
    lastViewProfiles: ProfileDto[];
    templates: MarkTemplateDto[];
    profilesWithProjects: SearchProfileDto[];
};

const initialState: InitialState = {
    isFetching: false,
    projects: [],
    profiles: [],
    feedbacks: [],
    lastView: [],
    lastViewFeedbacks: [],
    lastViewProfiles: [],
    templates: [],
    profilesWithProjects: []
};

export const qualityFeedback = createReducer(initialState, (builder) =>
    builder
        .addCase(getProjects.pending, (state) => ({...state, isFetching: true}))
        .addCase(getProjects.fulfilled, (state, action) => ({...state, isFetching: false, projects: action.payload}))
        .addCase(getProjects.rejected, (state) => ({...state, isFetching: false}))
        .addCase(getProfiles.pending, (state, action) => ({
            ...state,
            isFetching: true,
            profiles: state.profiles.filter((el) => el.projectId !== action.meta.arg.projectId)
        }))
        .addCase(getProfiles.fulfilled, (state, action) => ({
            ...state,
            isFetching: false,
            profiles: state.profiles.concat(action.payload)
        }))
        .addCase(getFeedbacks.pending, (state, action) => ({
            ...state,
            isFetching: true,
            feedbacks: state.feedbacks.filter((el) => el.projectId !== action.meta.arg.projectId)
        }))
        .addCase(getFeedbacks.fulfilled, (state, action) => ({
            ...state,
            isFetching: false,
            feedbacks: state.feedbacks.concat(action.payload)
        }))
        .addCase(updateFeedbacks, (state, action) => {
            const feedbacks = state.feedbacks.map(({projectId, feedbacks}) => {
                const updatedFeedbacks = action.payload.filter((el) => el.projectId === projectId);
                const notUpdatedFeedbacks = feedbacks.filter(
                    ({authorId, recipientId}) =>
                        !updatedFeedbacks.some((el) => el.authorId === authorId && el.recipientId === recipientId)
                );
                return {projectId, feedbacks: [...updatedFeedbacks, ...notUpdatedFeedbacks]};
            });
            return {...state, feedbacks};
        })
        .addCase(deleteFeedback.fulfilled, (state, action) => ({
            ...state,
            feedbacks: state.feedbacks.map((el) => {
                if (el.projectId === action.payload.projectId) {
                    return {...el, feedbacks: el.feedbacks.filter((el) => el.feedbackId !== action.payload.feedbackId)};
                } else {
                    return el;
                }
            })
        }))
        .addCase(getTemplates.pending, (state, action) => ({
            ...state,
            isFetching: true,
            templates: state.templates.filter((el) => el.projectId !== action.meta.arg.projectId)
        }))
        .addCase(getTemplates.fulfilled, (state, action) => ({
            ...state,
            isFetching: false,
            templates: state.templates.concat(action.payload)
        }))
        .addCase(getTemplates.rejected, (state) => ({...state, isFetching: false}))
        .addCase(searchProfilesWithProjects.pending, (state) => ({...state, isFetching: true}))
        .addCase(searchProfilesWithProjects.fulfilled, (state, action) => ({
            ...state,
            isFetching: false,
            profilesWithProjects: action.payload
        }))
        .addCase(searchProfilesWithProjects.rejected, (state) => ({...state, isFetching: false}))
        .addCase(createOptionalFeedback.fulfilled, (state, action) => ({
            ...state,
            projects: state.projects.concat(action.payload)
        }))
        .addCase(deleteOptionalFeedback.fulfilled, (state, action) => ({
            ...state,
            profiles: state.profiles.map((el) => {
                if (el.projectId === action.payload.projectId) {
                    return {...el, team: el.team.filter(({relationId}) => relationId !== action.payload.relationId)};
                } else {
                    return el;
                }
            })
        }))
        .addCase(clearProfilesWithProjects, (state) => ({...state, profilesWithProjects: []}))
        .addCase(getLastView.pending, (state, action) => ({
            ...state,
            lastView: state.lastView.filter((el) => el.projectId !== action.meta.arg.projectId)
        }))
        .addCase(getLastView.fulfilled, (state, action) => ({
            ...state,
            lastView: state.lastView.concat(action.payload)
        }))
        .addCase(getLastViewFeedbacks.fulfilled, (state, action) => ({
            ...state,
            lastViewFeedbacks: unionWith(state.lastViewFeedbacks, action.payload, isEqual)
        }))
        .addCase(getLastViewProfiles.fulfilled, (state, action) => ({
            ...state,
            lastViewProfiles: unionWith(state.lastViewProfiles, action.payload, isEqual)
        }))
        .addCase(updateLastView.fulfilled, (state, action) => ({
            ...state,
            lastView: state.lastView.map((el) => {
                if (el.id === action.payload) {
                    return {...el, new: false};
                } else {
                    return el;
                }
            })
        }))
);
